// NEVANU — Unified API Client
// Ersetzt 238x XMLHttpRequest mit einem einheitlichen fetch()-Wrapper
// Sendet automatisch JWT-Cookie (credentials: 'include')

import type { ApiResponse } from './types';

/** Base URL für API-Calls — leer = relative URLs (gleicher Host) */
const API_BASE = '';

/** CSRF-Token aus Cookie lesen (gesetzt von api/security.php) */
function getCsrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

/** Aktuelle Session-Daten aus localStorage lesen */
function getSessionData(): { user: string; pin: string } {
  try {
    const raw = localStorage.getItem('nevanuSession');
    if (raw) {
      const s = JSON.parse(raw);
      return { user: s.user || '', pin: s.pin || '' };
    }
  } catch {}
  return { user: '', pin: '' };
}

/**
 * Einheitlicher API-Call
 *
 * @param route  - API-Datei (z.B. 'team', 'buchungen', 'kunden')
 * @param action - Action-Parameter (z.B. 'list', 'save', 'delete')
 * @param params - GET-Parameter als Object
 * @param body   - POST-Body als Object (löst automatisch POST aus)
 * @returns      - Promise<ApiResponse<T>>
 *
 * Beispiel:
 *   const data = await api('buchungen', 'list', { datum: '2026-03-07' });
 *   const result = await api('kunden', 'save', {}, { name: 'Anna', email: '...' });
 */
export async function api<T = any>(
  route: string,
  action?: string,
  params?: Record<string, string | number | boolean>,
  body?: Record<string, any> | FormData
): Promise<ApiResponse<T>> {
  // Session-Daten für Legacy-Kompatibilität (bis JWT aktiv)
  const session = getSessionData();

  // URL zusammenbauen — B1 (Strang 2, 08.10.2026): ueber den Front-Controller api.php (zentrale Anmeldung, CSRF,
  // Modul-Riegel), nie direkt auf /api/<route>.php. Die Aliase news→nachrichten und todos→aufgaben loest api.php.
  const url = new URL(`${API_BASE}/api.php`, window.location.origin);
  url.searchParams.set('route', route);
  if (action) url.searchParams.set('action', action);
  if (session.user) url.searchParams.set('user', session.user);
  // B6 Schritt 3 (S2, 09.10.2026): keine PIN in der Adresse — der Server kennt die Person aus der Anmeldung und liest
  // «pin» nie aus der Adresse (tools/b6-adresse-probe.cjs). Auch ein «pin» in params fällt unten weg.

  // Zusätzliche GET-Parameter
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      url.searchParams.set(k, String(v));
    });
  }
  url.searchParams.delete('pin');

  // Request-Optionen
  const headers: Record<string, string> = {};

  // CSRF-Token bei schreibenden Requests mitsenden
  const csrfToken = getCsrfToken();
  if (csrfToken && body) {
    headers['X-XSRF-TOKEN'] = csrfToken;
  }

  const options: RequestInit = {
    credentials: 'include', // JWT-Cookie automatisch mitsenden
    headers,
  };

  if (body) {
    options.method = 'POST';
    if (body instanceof FormData) {
      options.body = body;
    } else {
      headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }
  }

  try {
    const response = await fetch(url.toString(), options);

    // Rate Limit
    if (response.status === 429) {
      return { ok: false, error: 'Zu viele Anfragen. Bitte warte kurz.' };
    }

    // Server-Fehler
    if (!response.ok) {
      return { ok: false, error: `Server-Fehler: ${response.status}` };
    }

    // JSON parsen
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      return await response.json();
    }

    // Fallback: Text-Response
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch {
      return { ok: false, error: 'Ungültige Server-Antwort' };
    }
  } catch (err: any) {
    // Netzwerk-Fehler (offline, timeout, etc.)
    if (!navigator.onLine) {
      return { ok: false, error: 'Keine Internetverbindung' };
    }
    return { ok: false, error: err.message || 'Netzwerk-Fehler' };
  }
}

/**
 * Upload-Wrapper für Dateien
 */
export async function apiUpload(
  file: File,
  type: string = 'profile',
  extraParams?: Record<string, string>
): Promise<ApiResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);

  if (extraParams) {
    Object.entries(extraParams).forEach(([k, v]) => formData.append(k, v));
  }

  return api('upload', undefined, undefined, formData);
}

/**
 * Legacy-Kompatibilität: XHR-ähnlicher Wrapper
 * Für bestehenden Code der schrittweise migriert wird
 */
export function apiLegacy(
  route: string,
  action: string,
  params: Record<string, any>,
  callback: (ok: boolean, data: any) => void
): void {
  const getParams: Record<string, string> = {};
  const postBody: Record<string, any> = {};

  Object.entries(params).forEach(([k, v]) => {
    if (typeof v === 'object' && v !== null) {
      postBody[k] = v;
    } else {
      getParams[k] = String(v);
    }
  });

  const hasPostData = Object.keys(postBody).length > 0;

  api(route, action, getParams, hasPostData ? postBody : undefined)
    .then(data => callback(data.ok !== false, data))
    .catch(() => callback(false, { ok: false, error: 'Netzwerk-Fehler' }));
}
